import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttemptReveal, ClipPlayback } from '@proref/types';
import { useRecordView } from '../../hooks/use-clips';
import { useRevealQuery, useSubmitAttempt } from '../../hooks/use-attempts';
import { useToggleBookmark } from '../../hooks/use-bookmarks';
import { notifyError, notifySuccess } from '../../lib/toast';
import { Spinner } from '../shared/Spinner';
import { Button } from '../shared/Button';
import { Icon } from '../shared/Icons';
import { AnimatedNumber } from '../shared/AnimatedNumber';
import { VideoPlayer } from '../shared/VideoPlayer';
import { DiscussionThread } from '../discussion/DiscussionThread';

interface ClipAnswerPanelProps {
  // The clip to play + answer, with its signed playback URL and (for a referee) viewer state.
  // null means there is nothing to play — see `loading` for how that is rendered.
  clip: ClipPlayback | null;
  // Whether a clip is still on its way. Without this the panel can't tell "the playback URL is
  // still being minted" from "this library has no clips at all", and a referee whose federation
  // has nothing published sat on a spinner forever. Defaults to false, so the honest empty state
  // is the fallback and a hang is never the default failure mode.
  loading?: boolean;
  // Badges the player as the referee's assigned daily clip.
  isDaily?: boolean;
  // Whether to mount the discussion thread under the decision flow. The full clips page shows it;
  // the compact Home hero hides it (discussion lives on the full clip page there).
  showDiscussion?: boolean;
  // Refetch the playback query when the signed URL expires mid-session (1h TTL). Passed straight to
  // the player, which fires it on a network error to mint a fresh URL. Optional — callers without a
  // refetch handle (or that don't care) simply get the old behaviour (the error surfaces).
  onSourceExpired?: () => void;
}

// The self-contained "watch → decide → reveal" experience for one clip, as a card: eyebrow + title +
// Save, the player, and the decision/reveal flow (optionally the discussion thread). Shared by the
// Video clips page (main player) and the Home hero (answer today's clip inline) so both drive the
// exact same answer lifecycle — answering here records the attempt that advances the streak.
export function ClipAnswerPanel({
  clip,
  isDaily = false,
  showDiscussion = true,
  loading = false,
  onSourceExpired,
}: ClipAnswerPanelProps) {
  const { t } = useTranslation();
  // Declared before the early return (hooks can't be conditional). Logs a view each time the player
  // starts a watch; fire-and-forget, the count ticks up via the cache patch in the hook.
  const view = useRecordView();

  // Still fetching → spinner. Settled with nothing → say so, rather than spinning forever.
  if (!clip) {
    return (
      <div className="rounded border border-line bg-surface p-8 shadow-1">
        {loading ? (
          <div className="grid place-items-center py-16 text-muted">
            <Spinner className="h-6 w-6" />
            <div className="mt-3 text-sm">{t('clips.answerPanel.loading')}</div>
          </div>
        ) : (
          <div className="grid place-items-center py-16 text-center">
            <Icon.clip className="h-7 w-7 text-muted" />
            <p className="mt-3 text-sm font-medium text-ink">{t('clips.answerPanel.emptyTitle')}</p>
            <p className="mt-1 max-w-xs text-xs text-muted">{t('clips.answerPanel.emptyBody')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded border border-line bg-surface p-5 shadow-1">
      {/* Eyebrow row: the "Daily clip" label on the left, the rule pill pushed full right. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
          <Icon.spark className="h-3.5 w-3.5 text-accent" />
          {isDaily ? t('clips.answerPanel.dailyClip') : t('clips.answerPanel.nowPlaying')}
          {/* Raw playback count — every watch, repeats included (clips.views). */}
          <span className="text-line-3">·</span>
          <span>{t('clips.answerPanel.views', { count: clip.views })}</span>
        </div>
        {clip.rule_article && (
          <span className="flex-none rounded-btn border border-line-2 bg-surface-2 px-2.5 py-1 text-[11px] font-medium text-ink-2">
            {t('common.ruleLabel', { number: clip.rule_article.article_number })}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-start justify-between gap-3">
        {/* min-w-0 + break-words: titles default to the upload's filename, which can be one long
            unbroken token (e.g. a hashed name). As a flex item the heading would otherwise refuse to
            shrink below that word and push past the card, taking the Save button with it. */}
        <h2 className="min-w-0 break-words font-display text-2xl leading-tight text-ink">{clip.title}</h2>
        <SaveButton clip={clip} />
      </div>

      <div className="relative mt-4 overflow-hidden rounded border border-line-2 bg-bg-2">
        {clip.playback_url ? (
          <VideoPlayer
            src={clip.playback_url}
            poster={clip.poster_url ?? undefined}
            onFirstPlay={() => view.mutate(clip.id)}
            onSourceExpired={onSourceExpired}
          />
        ) : (
          <div className="grid aspect-video place-items-center text-sm text-muted">
            {t('clips.answerPanel.playbackUnavailable')}
          </div>
        )}
        {isDaily && (
          <div className="absolute left-3 top-3 rounded-btn bg-danger px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-bg shadow-1">
            {t('clips.answerPanel.dailyBadge')}
          </div>
        )}
      </div>

      {/* Decision → reveal → discussion gate. Keyed by clip id so all answer state resets cleanly
          when the player swaps to a different clip. */}
      <ClipDecisionFlow key={clip.id} clip={clip} showDiscussion={showDiscussion} />
    </div>
  );
}

// ─── Decision / reveal flow ─────────────────────────────────────────────────────

// Owns the answer lifecycle for one clip. Three states:
//   1. unanswered           → DecisionPanel (options enabled, submit)
//   2. answered (now/before) → RevealPanel (correct call + commentary + distribution graph)
//   3. "Answer again"        → back to DecisionPanel; the next submit is a practice repeat
// The clip is answerable every time — repeats are recorded, but only the first scores.
function ClipDecisionFlow({ clip, showDiscussion }: { clip: ClipPlayback; showDiscussion: boolean }) {
  const { t } = useTranslation();
  // Whether the referee answered this clip in a PRIOR session (server-derived; carries no answer
  // fields). Drives whether we fetch the persisted reveal on load.
  const answeredBefore = clip.viewer?.has_answered ?? false;

  const [selected, setSelected] = useState<string | null>(null);
  // Reveal captured from a submit THIS session — takes precedence over the fetched one.
  const [localReveal, setLocalReveal] = useState<AttemptReveal | null>(null);
  // The referee chose "Answer again": force the decision panel even though a reveal exists.
  const [retrying, setRetrying] = useState(false);

  const submit = useSubmitAttempt();
  // Fetch the persisted reveal only when they answered before and we don't already have a fresher
  // local one (and aren't mid-retry) — otherwise the API would 403 or we'd waste a call.
  const revealQuery = useRevealQuery(clip.id, answeredBefore && !localReveal && !retrying);
  const reveal = localReveal ?? (answeredBefore ? revealQuery.data ?? null : null);

  const showReveal = reveal != null && !retrying;
  // Avoid flashing the decision panel while the persisted reveal is still loading.
  const loadingReveal = answeredBefore && !localReveal && !retrying && revealQuery.isLoading;
  // Discussion unlocks the moment they've answered (this session or before).
  const discussionUnlocked = answeredBefore || localReveal != null;

  async function onSubmit() {
    if (!selected) return;
    try {
      const result = await submit.mutateAsync({ clip_id: clip.id, selected_option: selected });
      setLocalReveal(result);
      setRetrying(false);
      setSelected(null);
      notifySuccess(
        result.is_correct ? t('clips.answerPanel.correctToast') : t('clips.answerPanel.recordedToast'),
      );
    } catch (err) {
      notifyError(err, t('clips.answerPanel.answerErrorToast'));
    }
  }

  return (
    <div className="mt-5">
      {loadingReveal ? (
        <div className="grid place-items-center py-8 text-muted">
          <Spinner className="h-5 w-5" />
        </div>
      ) : showReveal ? (
        <RevealPanel reveal={reveal!} onAnswerAgain={() => { setRetrying(true); setSelected(null); }} />
      ) : (
        <DecisionPanel
          options={clip.options}
          selected={selected}
          onSelect={setSelected}
          onSubmit={onSubmit}
          submitting={submit.isPending}
        />
      )}

      {showDiscussion && <DiscussionThread clipId={clip.id} unlocked={discussionUnlocked} />}
    </div>
  );
}

// The player's Save (bookmark) toggle. Reflects clip.viewer.is_bookmarked and flips it; the filled
// icon + "Saved" label signal the on state. viewer is absent for super_admin (who don't reach this
// surface), so default to not-bookmarked.
function SaveButton({ clip }: { clip: ClipPlayback }) {
  const { t } = useTranslation();
  const bookmarked = clip.viewer?.is_bookmarked ?? false;
  const toggle = useToggleBookmark(clip.id);

  async function onClick() {
    // No success toast: the optimistic button flip IS the confirmation — a toast would pop in after
    // the request lands and read as lag. Only a FAILURE is worth surfacing (the button rolls back).
    try {
      await toggle.mutateAsync(bookmarked);
    } catch (err) {
      notifyError(err, t('clips.answerPanel.bookmarkErrorToast'));
    }
  }

  return (
    // Not disabled while pending — the label flips optimistically, so the button stays snappy and
    // re-clickable; a failed request rolls the state back (see useToggleBookmark).
    <Button variant={bookmarked ? 'accent' : 'default'} size="sm" onClick={onClick} aria-pressed={bookmarked}>
      <Icon.bookmark className="h-3.5 w-3.5" />
      {bookmarked ? t('clips.answerPanel.saved') : t('clips.answerPanel.save')}
    </Button>
  );
}

interface DecisionPanelProps {
  options: string[];
  selected: string | null;
  onSelect: (option: string) => void;
  onSubmit: () => void;
  submitting: boolean;
}

function DecisionPanel({ options, selected, onSelect, onSubmit, submitting }: DecisionPanelProps) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-ink-2">
        <Icon.spark className="h-4 w-4 text-accent" />
        {t('clips.answerPanel.decisionPrompt')}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {options.map((opt) => {
          const active = selected === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onSelect(opt)}
              aria-pressed={active}
              className={[
                'rounded-btn border-[1.5px] px-3.5 py-2.5 text-left text-sm transition-colors',
                active
                  ? 'border-accent bg-accent-tint text-ink'
                  : 'border-line-2 bg-surface text-ink-2 hover:border-line-3 hover:bg-surface-2',
              ].join(' ')}
            >
              {opt}
            </button>
          );
        })}
      </div>
      <div className="mt-3">
        <Button variant="accent" size="md" onClick={onSubmit} disabled={!selected || submitting}>
          {submitting ? t('clips.answerPanel.submitting') : t('clips.answerPanel.submitAnswer')}
        </Button>
      </div>
    </div>
  );
}

interface RevealPanelProps {
  reveal: AttemptReveal;
  onAnswerAgain: () => void;
}

function RevealPanel({ reveal, onAnswerAgain }: RevealPanelProps) {
  const { t } = useTranslation();
  // Single source for the correct/incorrect colour pairing — the header strip, icon badge and
  // decision label all key off it, so the two states can never drift out of sync visually.
  const tone = reveal.is_correct
    ? { text: 'text-success', tint: 'bg-success-tint', solid: 'bg-success' }
    : { text: 'text-danger', tint: 'bg-danger-tint', solid: 'bg-danger' };

  return (
    <div className="overflow-hidden rounded-lg border border-line-2 bg-surface-2">
      {/* Result header: icon badge + verdict, coloured by outcome — the first thing a referee's
          eye lands on after submitting. */}
      <div className={['flex items-center gap-3 px-4 py-3.5', tone.tint].join(' ')}>
        <span className={['grid h-8 w-8 flex-none place-items-center rounded-full text-bg', tone.solid].join(' ')}>
          {reveal.is_correct ? <Icon.check className="h-4 w-4" /> : <Icon.close className="h-3.5 w-3.5" />}
        </span>
        <div>
          <div className={['text-sm font-semibold', tone.text].join(' ')}>
            {reveal.is_correct ? t('clips.answerPanel.correctCall') : t('clips.answerPanel.notQuite')}
          </div>
          <div className="text-xs text-muted">
            {reveal.is_correct ? t('clips.answerPanel.matchedCall') : t('clips.answerPanel.seeCorrectBelow')}
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* The decision itself, pulled out into its own highlighted row so it reads as the
            answer to the question, not a caption under the banner above. */}
        <div className="flex items-start gap-2.5 rounded border border-line-2 bg-surface px-3.5 py-3">
          <Icon.whistle className="mt-0.5 h-4 w-4 flex-none text-accent" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">
              {t('clips.answerPanel.correctDecision')}
            </div>
            <div className="mt-0.5 font-display text-base font-bold leading-snug text-ink">
              {reveal.correct_decision}
            </div>
          </div>
        </div>

        {/* Commentary as a quoted aside (accent rule, no box) — it reads as the rule-grounded
            "why", distinct from the decision card above it. */}
        {reveal.commentary_text && (
          <p className="mt-3 border-l-2 border-accent/40 pl-3 text-sm leading-relaxed text-ink-2">
            {reveal.commentary_text}
          </p>
        )}

        <AnswerDistribution reveal={reveal} />

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-line-2 pt-3.5">
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Icon.flask className="h-3.5 w-3.5 flex-none" />
            {t('clips.answerPanel.practiceNote')}
          </p>
          <Button variant="ghost" size="sm" onClick={onAnswerAgain} className="flex-none">
            <Icon.arrow className="h-3.5 w-3.5" />
            {t('clips.answerPanel.answerAgain')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// The "how did referees decide" graph — one horizontal bar per option, first-attempt votes only.
// The correct option is tinted green; the referee's own vote is tagged "You".
function AnswerDistribution({ reveal }: { reveal: AttemptReveal }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
          <Icon.chart className="h-3.5 w-3.5" />
          {t('clips.answerPanel.howAnswered')}
        </div>
        <span className="text-[11px] text-muted">
          <AnimatedNumber value={reveal.total_first_attempts} />{' '}
          {t('clips.answerPanel.answers', { count: reveal.total_first_attempts })}
        </span>
      </div>

      <div className="mt-3 space-y-2.5">
        {reveal.distribution.map((d) => {
          const isCorrect = d.option === reveal.correct_decision;
          const isYours = d.option === reveal.selected_option;
          return (
            <div key={d.option}>
              <div className="flex items-center justify-between text-xs">
                <span className={['flex items-center gap-1.5', isCorrect ? 'font-semibold text-ink' : 'text-ink-2'].join(' ')}>
                  {d.option}
                  {isYours && (
                    // Tagged with the same tone as the correctness banner above — a wrong pick
                    // reads as "you, in red" here too, instead of a neutral badge either way.
                    <span
                      className={[
                        'rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bg',
                        isCorrect ? 'bg-success' : 'bg-danger',
                      ].join(' ')}
                    >
                      You
                    </span>
                  )}
                </span>
                <span className="tabular-nums text-muted">
                  <AnimatedNumber value={d.percent} />% (<AnimatedNumber value={d.count} />)
                </span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-surface-3">
                <div
                  className={['h-full rounded-full', isCorrect ? 'bg-success' : 'bg-line-3'].join(' ')}
                  // Inline width is unavoidable — the bar length is data-driven (the percent).
                  style={{ width: `${d.percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
